import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import type { EmployeeRecord } from "@/lib/hr/employeesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, User } from "lucide-react";
import { useMemo } from "react";

export function employeeDisplayName(employee: EmployeeRecord): string {
  return (employee.fullName || `${employee.firstName} ${employee.lastName}`).trim() || employee.id;
}

export function employeePickerLabel(employee: EmployeeRecord): string {
  const name = employeeDisplayName(employee);
  return employee.employeeCode ? `${employee.employeeCode} · ${name}` : name;
}

export function employeeInitials(employee: EmployeeRecord): string {
  const first = (employee.firstName || "").trim();
  const last = (employee.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (employee.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

export function employeeSubtitle(employee: EmployeeRecord): string {
  const parts: string[] = [];
  if (employee.departmentName?.trim()) parts.push(employee.departmentName.trim());
  if (employee.designationName?.trim()) parts.push(employee.designationName.trim());
  return parts.join(" · ");
}

export function isActiveEmployee(employee: EmployeeRecord): boolean {
  const status = (employee.employeeStatus ?? "").trim();
  return status === "" || status.toLowerCase() === "active";
}

type EmployeeSelectProps = {
  value: string;
  onChange: (id: string) => void;
  employees: EmployeeRecord[];
  /** Override the options list (skips activeOnly filtering). */
  options?: EmployeeRecord[];
  placeholder?: string;
  searchPlaceholder?: string;
  clearable?: boolean;
  clearLabel?: string;
  /** Value emitted when clearable "all" option is chosen. Default "". */
  clearValue?: string;
  disabled?: boolean;
  activeOnly?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  emptyLabel?: string;
};

export function EmployeeSelect({
  value,
  onChange,
  employees,
  options,
  placeholder = "Select employee…",
  searchPlaceholder = "Search name, code or department…",
  clearable = false,
  clearLabel = "All employees",
  clearValue = "",
  disabled = false,
  activeOnly = true,
  triggerClassName,
  contentClassName,
  emptyLabel = "No employee found.",
}: EmployeeSelectProps) {
  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);

  const list = useMemo(() => {
    if (options) return options;
    if (!activeOnly) return employees;
    return employees.filter(isActiveEmployee);
  }, [options, employees, activeOnly]);

  const selected = useMemo(() => {
    if (!value || value === clearValue) return null;
    return employees.find((e) => e.id === value) ?? list.find((e) => e.id === value) ?? null;
  }, [employees, list, value, clearValue]);

  const isCleared = clearable && (!value || value === clearValue);

  return (
    <Popover modal open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "glass-input h-9 w-full justify-between px-3 font-normal",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate min-w-0">
              <Avatar className="h-5 w-5 text-[8px]">
                {selected.photoDataUrl ? (
                  <AvatarImage src={selected.photoDataUrl} alt={employeeDisplayName(selected)} />
                ) : null}
                <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                  {employeeInitials(selected)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">{employeePickerLabel(selected)}</span>
            </span>
          ) : (
            <span className="truncate text-left">{clearable ? clearLabel : placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("z-[100] w-[var(--radix-popover-trigger-width)] p-0", contentClassName)}
        align="start"
        sideOffset={4}
      >
        <Command
          shouldFilter
          filter={(item, query) => {
            const q = query.trim().toLowerCase();
            if (!q) return 1;
            return item.toLowerCase().includes(q) ? 1 : 0;
          }}
        >
          <CommandInput {...commandInputProps} placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {clearable ? (
                <CommandItem
                  value={`__all__ ${clearLabel}`}
                  onSelect={() => {
                    onChange(clearValue);
                    setOpen(false);
                  }}
                  className="gap-2"
                >
                  <Check className={cn("h-3.5 w-3.5 shrink-0", isCleared ? "opacity-100" : "opacity-0")} />
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{clearLabel}</span>
                </CommandItem>
              ) : null}
              {list.map((e) => {
                const label = employeePickerLabel(e);
                const subtitle = employeeSubtitle(e);
                const isSelected = value === e.id;
                const searchValue = [label, e.employeeCode, e.departmentName, e.designationName, e.id]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <CommandItem
                    key={e.id}
                    value={searchValue}
                    onSelect={() => {
                      onChange(e.id);
                      setOpen(false);
                    }}
                    className="gap-2"
                  >
                    <Check className={cn("h-3.5 w-3.5 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                    <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                      {e.photoDataUrl ? (
                        <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                      ) : null}
                      <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                        {employeeInitials(e)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium">{label}</span>
                      {subtitle ? (
                        <span className="truncate text-[11px] leading-tight opacity-60">{subtitle}</span>
                      ) : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
